import { singleline } from "@core/foundation/string";
import { api, apiEndpoint } from "@core/ports/http";
import { z } from "@core/zod";

import { API_DOMAIN } from "@platform/core";

import { ACCOUNT_SERVICE } from "../config/index.ts";
import { GithubConnection } from "../models/index.ts";

const GithubRepoSchema = z.object(
  {
    fullName: z.string(),
    githubRepoId: z.int().nonnegative(),
    name: z.string(),
    private: z.boolean(),
  },
  {
    fullName: "The repo's full name, as owner/name",
    githubRepoId: "The repo's GitHub id",
    name: "The repo's name",
    private: "Whether the repo is private",
  },
);

export const githubApi = api(
  { domain: API_DOMAIN, path: `${ACCOUNT_SERVICE.LABEL}/github` },
  {
    completeInstall: apiEndpoint({
      body: z.object(
        { code: z.string(), installationId: z.int().nonnegative() },
        {
          code: "The OAuth code GitHub returned to the install callback",
          installationId: GithubConnection.descriptions.installationId,
        },
      ),
      response: z.object(
        { githubConnection: GithubConnection.schema },
        { githubConnection: "The connection the installation created" },
      ),
    }),
    disconnect: apiEndpoint({
      body: z.object(
        { githubAccountId: z.int().nonnegative() },
        { githubAccountId: "The id of the GitHub account to disconnect" },
      ),
      response: z.object(
        { githubConnection: GithubConnection.schema },
        { githubConnection: "The connection, now disconnected" },
      ),
    }),
    getAllGithubConnections: apiEndpoint({
      response: z.object(
        {
          items: z.array(
            z.object(
              {
                githubConnection: GithubConnection.schema,
                repos: z.array(GithubRepoSchema),
              },
              {
                githubConnection: "The connection",
                repos: singleline`
                  The repos the installation can access, empty for a
                  disconnected connection
                `,
              },
            ),
          ),
        },
        { items: "The signed-in user's GitHub connections" },
      ),
    }),
    getInstallUrl: apiEndpoint({
      body: z.object(
        { session: z.safeId() },
        {
          session: singleline`
            The connect session the app started, passed back to the install
            callback as its state
          `,
        },
      ),
      response: z.object(
        { installUrl: z.url() },
        { installUrl: "The GitHub App install URL to send the browser to" },
      ),
    }),
  },
);
